"use client";
import { Box, Typography, Container, Avatar, Paper } from "@mui/material";
import Image from "next/image";
import ScrollReveal from "./ScrollReveal";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

export default function TestimonialsStatsFounderMember() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const testimonialsRaw = t("testimonialsStatsFounder.testimonials", {
    returnObjects: true,
  });
  const testimonials = Array.isArray(testimonialsRaw) ? testimonialsRaw : [];

  const statsRaw = t("testimonialsStatsFounder.stats", { returnObjects: true });
  const stats = Array.isArray(statsRaw) ? statsRaw : [];

  const testimonialImages = [
    IMAGES.HADEESA_AVATAR,
    IMAGES.MAHA_AVATAR,
    IMAGES.RAMIZ_AVATAR,
  ];

  const statImages = [
    IMAGES.TSFM_ONE_IMG,
    IMAGES.TSFM_TWO_IMG,
    IMAGES.TSFM_THREE_IMG,
    IMAGES.TSFM_FOUR_IMG,
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        py: { xs: 6, sm: 8, md: 1, lg: 1, xl: 10 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <ScrollReveal delay={0.1} duration={1.0}>
          <Box
            sx={{
              textAlign: isArabic
                ? { xs: "right", sm: "right", md: "right", lg: "center" }
                : { xs: "left", sm: "left", md: "left", lg: "center" },
              mb: { xs: 5, sm: 6, md: 8 },
            }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: "#111827",
                mb: 1,
                fontSize: {
                  xs: "1.5rem",
                  sm: "1.75rem",
                  md: "1.7rem",
                  lg: "1.7rem",
                  xl: "2rem",
                },
                lineHeight: 1.3,
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("testimonialsStatsFounder.title")}
            </Typography>
            <Typography
              sx={{
                color: "#4B5563",
                fontSize: {
                  xs: "0.9rem",
                  sm: "0.95rem",
                  md: "0.95rem",
                  lg: "0.95rem",
                  xl: "1.1rem",
                },
                maxWidth: { xs: "100%", md: "990px" },
                mx: { xs: 0, sm: 0, md: "auto" },
                lineHeight: 1.6,
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
              }}
            >
              {t("testimonialsStatsFounder.subtitle")}
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Testimonials */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: { xs: 2.5, sm: 3, md: 3 },
            mb: { xs: 5, sm: 6, md: 8 },
          }}
        >
          {testimonials.map((testimonial, index) => (
            <ScrollReveal key={index} delay={0.2 + index * 0.1} duration={1.0}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  borderRadius: 3,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #F9FAFB",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                  },
                }}
              >
                {/* Profile */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    mb: 2.5,
                    flexDirection: "row",
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 45, sm: 50 },
                      height: { xs: 45, sm: 50 },
                      flexShrink: 0,
                    }}
                  >
                    <Image
                      src={testimonialImages[index]}
                      alt={testimonial.alt}
                      width={50}
                      height={50}
                      style={{ borderRadius: "50%", objectFit: "cover" }}
                    />
                  </Avatar>
                  <Box sx={{ flex: 1, minHeight: "80px" }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#111827",
                        fontFamily: "var(--font-outfit)",
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        mb: 0.5,
                        textAlign: isArabic ? "right" : "left",
                      }}
                    >
                      {testimonial.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#4B5563",
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        fontWeight: 400,
                        fontFamily: "var(--font-outfit)",
                        textAlign: isArabic ? "right" : "left",
                      }}
                    >
                      {testimonial.role}
                    </Typography>
                  </Box>
                </Box>

                {/* Quote */}
                <Typography
                  sx={{
                    color: "#374151",
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    fontFamily: "var(--font-outfit)",
                    mb: 2.5,
                    flexGrow: 1,
                    fontWeight: 400,
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {testimonial.quote}
                </Typography>

                {/* Rating */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: isArabic ? "flex-start" : "flex-start",
                    mt: "auto",
                  }}
                >
                  <Image
                    src={IMAGES.FIVE_STARS_IMG}
                    alt="stars"
                    width={110}
                    height={24}
                    style={{ objectFit: "contain" }}
                  />
                </Box>
              </Paper>
            </ScrollReveal>
          ))}
        </Box>
      </Container>

      {/* Stats Section */}
      <ScrollReveal delay={0.3} duration={1.2}>
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #9810FA 0%, #8200DB 50%, #5916B8 100%)",
            py: { xs: 4, sm: 5, md: 7 },
            px: { xs: 2, sm: 3, md: 6 },
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "repeat(4, 1fr)",
                },
                gap: { xs: 3, sm: 4, md: 6 },
              }}
            >
              {stats.map((stat, index) => (
                <Box
                  key={index}
                  sx={{
                    textAlign: isArabic
                      ? { xs: "right", sm: "right", md: "center" }
                      : { xs: "left", sm: "left", md: "center" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isArabic
                      ? { xs: "flex-start", sm: "flex-start", md: "center" }
                      : { xs: "flex-start", sm: "flex-start", md: "center" },
                    minHeight: { lg: "280px", xl: "280px" },
                  }}
                >
                  <Box
                    sx={{
                      mb: isArabic ? 2 : { xs: 1, sm: 1.2, md: 1.5 },
                      width: { xs: 50, sm: 60, md: 70 },
                      height: { xs: 50, sm: 60, md: 70 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      alignSelf: isArabic
                        ? { xs: "flex-start", sm: "flex-start", md: "center" }
                        : { xs: "flex-start", sm: "flex-start", md: "center" },
                    }}
                  >
                    <Image
                      src={statImages[index]}
                      alt={stat.title}
                      width={70}
                      height={70}
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "white",
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2rem" },
                      lineHeight: 1.2,
                      mb: isArabic ? 2 : { xs: 0.5, sm: 0.8, md: 1 },
                      fontFamily: "var(--font-outfit)",
                      mt: { xs: "1%", sm: "1.5%", md: "2%" },
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "white",
                      fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.1rem" },
                      lineHeight: 1.3,
                      mb: { xs: 1, sm: 1.2, md: 1.5 },
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#FFFFFF",
                      fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.9rem" },
                      fontWeight: 400,
                      lineHeight: 1.5,
                      fontFamily: "var(--font-outfit)",
                      opacity: 0.95,
                    }}
                  >
                    {stat.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      </ScrollReveal>
    </Box>
  );
}
